import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Layout({ children }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [strategiesOpen, setStrategiesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setStrategiesOpen(false);
  }, [location]);

  const strategies = [
    { symbol: 'BTC', name: 'Bitcoin', path: '/strategies/btc.html' },
    { symbol: 'ETH', name: 'Ethereum', path: '/strategies/eth.html' },
    { symbol: 'XRP', name: 'XRP', path: '/strategies/xrp.html' },
    { symbol: 'SOL', name: 'Solana', path: '/strategies/sol.html' },
    { symbol: 'DOGE', name: 'Dogecoin', path: '/strategies/doge.html' },
    { symbol: 'BCH', name: 'Bitcoin Cash', path: '/strategies/bch.html' },
    { symbol: 'LINK', name: 'Chainlink', path: '/strategies/link.html' },
    { symbol: 'XLM', name: 'Stellar', path: '/strategies/xlm.html' },
    { symbol: 'LTC', name: 'Litecoin', path: '/strategies/ltc.html' },
    { symbol: 'AVAX', name: 'Avalanche', path: '/strategies/avax.html' },
    { symbol: 'ZEC', name: 'Zcash', path: '/strategies/zec.html' },
  ];

  // Portfolio Tracker is a single consolidated overview page (v10.2).
  // It covers every pair traded by the 3d-timeframe framework, for both
  // /USDT and /BTC. The former per-pair tracker pages were superseded.
  const trackerPath = '/tracker/index.html';

  const navLinkStyles = {
    color: '#a8c5e6',
    textDecoration: 'none',
    fontSize: '0.85rem',
    fontWeight: 500,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    padding: '0.5rem 0',
    transition: 'color 0.3s ease',
  };

  const activeLinkStyles = {
    ...navLinkStyles,
    color: '#4a9eff',
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0a1628 0%, #0d2847 50%, #1a3a5c 100%)',
      color: '#ffffff',
      fontFamily: "'Helvetica Neue', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    }}>
      {/* Header */}
      <header style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        padding: '1rem 5%',
        // When the mobile menu is open, force a fully opaque background so the
        // expanded menu obscures the page content underneath. Otherwise keep
        // the original scroll-driven behavior (transparent at top, frosted on
        // scroll).
        background: mobileMenuOpen
          ? '#0a1628'
          : scrolled
            ? 'rgba(10, 22, 40, 0.95)'
            : 'transparent',
        backdropFilter: mobileMenuOpen ? 'none' : scrolled ? 'blur(10px)' : 'none',
        borderBottom: (scrolled || mobileMenuOpen) ? '1px solid rgba(74, 158, 255, 0.1)' : 'none',
        // When the mobile menu is open the header becomes the menu container
        // and must be scrollable on short viewports (e.g. when the strategies
        // dropdown is expanded). Otherwise let it size to its content.
        maxHeight: mobileMenuOpen ? '100vh' : 'none',
        overflowY: mobileMenuOpen ? 'auto' : 'visible',
        transition: 'background 0.3s ease, border-bottom 0.3s ease',
      }}>
        <nav style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          maxWidth: '1400px',
          margin: '0 auto',
        }}>
          {/* Logo */}
          <Link to="/" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            textDecoration: 'none',
          }}>
            <svg width="36" height="36" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" aria-label="Parametrix Capital">
              <rect width="100" height="100" rx="18" fill="#4a9eff" />
              <path d="M28 24 h10 v52 h-10 z M38 24 h12 a13 13 0 0 1 0 26 h-12 z" fill="#ffffff" />
              <path d="M48 14 a36 36 0 0 1 0 72" fill="none" stroke="#ffffff" strokeWidth="5" strokeLinecap="round" />
            </svg>
            <span style={{
              color: '#ffffff',
              fontWeight: 600,
              fontSize: '1.1rem',
              letterSpacing: '0.02em',
            }}>
              PARAMETRIX
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '2.5rem',
          }} className="desktop-nav">
            <Link 
              to="/" 
              style={location.pathname === '/' ? activeLinkStyles : navLinkStyles}
              onMouseEnter={e => e.target.style.color = '#4a9eff'}
              onMouseLeave={e => e.target.style.color = location.pathname === '/' ? '#4a9eff' : '#a8c5e6'}
            >
              Home
            </Link>
            
            {/* Strategies Dropdown */}
            <div 
              style={{ position: 'relative' }}
              onMouseEnter={() => setStrategiesOpen(true)}
              onMouseLeave={() => setStrategiesOpen(false)}
            >
              <button style={{
                ...navLinkStyles,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
              }}>
                Strategies
                <svg 
                  width="10" 
                  height="6" 
                  viewBox="0 0 10 6" 
                  fill="none" 
                  style={{
                    transform: strategiesOpen ? 'rotate(180deg)' : 'rotate(0)',
                    transition: 'transform 0.2s ease',
                  }}
                >
                  <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              
              {/* Dropdown Menu */}
              <div style={{
                position: 'absolute',
                top: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                paddingTop: '0.75rem',
                opacity: strategiesOpen ? 1 : 0,
                visibility: strategiesOpen ? 'visible' : 'hidden',
                transition: 'opacity 0.2s ease, visibility 0.2s ease',
              }}>
                <div style={{
                  background: 'rgba(13, 30, 50, 0.98)',
                  border: '1px solid rgba(74, 158, 255, 0.2)',
                  borderRadius: '12px',
                  padding: '0.75rem',
                  minWidth: '280px',
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
                  backdropFilter: 'blur(20px)',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '0.25rem',
                }}>
                  {strategies.map((strategy) => (
                    <a
                      key={strategy.symbol}
                      href={strategy.path}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.6rem 0.75rem',
                        borderRadius: '6px',
                        textDecoration: 'none',
                        color: '#a8c5e6',
                        fontSize: '0.85rem',
                        transition: 'background 0.2s ease, color 0.2s ease',
                      }}
                      onMouseEnter={e => {
                        e.target.style.background = 'rgba(74, 158, 255, 0.1)';
                        e.target.style.color = '#ffffff';
                      }}
                      onMouseLeave={e => {
                        e.target.style.background = 'transparent';
                        e.target.style.color = '#a8c5e6';
                      }}
                    >
                      <span style={{
                        fontWeight: 600,
                        color: '#4a9eff',
                        minWidth: '45px',
                      }}>
                        {strategy.symbol}
                      </span>
                      <span style={{ color: '#6b9bc9', fontSize: '0.8rem' }}>
                        {strategy.name}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <Link 
              to="/service" 
              style={location.pathname === '/service' ? activeLinkStyles : navLinkStyles}
              onMouseEnter={e => e.target.style.color = '#4a9eff'}
              onMouseLeave={e => e.target.style.color = location.pathname === '/service' ? '#4a9eff' : '#a8c5e6'}
            >
              Our Service
            </Link>

            {/* Portfolio Tracker — single consolidated overview page */}
            <a
              href={trackerPath}
              style={navLinkStyles}
              onMouseEnter={e => e.target.style.color = '#4a9eff'}
              onMouseLeave={e => e.target.style.color = '#a8c5e6'}
            >
              Portfolio Tracker
            </a>

            <Link 
              to="/members" 
              style={location.pathname.startsWith('/members') ? activeLinkStyles : navLinkStyles}
              onMouseEnter={e => e.target.style.color = '#4a9eff'}
              onMouseLeave={e => e.target.style.color = location.pathname.startsWith('/members') ? '#4a9eff' : '#a8c5e6'}
            >
              Members
            </Link>

            <Link 
              to="/subscription" 
              style={location.pathname === '/subscription' ? activeLinkStyles : navLinkStyles}
              onMouseEnter={e => e.target.style.color = '#4a9eff'}
              onMouseLeave={e => e.target.style.color = location.pathname === '/subscription' ? '#4a9eff' : '#a8c5e6'}
            >
              Subscribe
            </Link>

            <Link 
              to="/contact" 
              style={location.pathname === '/contact' ? activeLinkStyles : navLinkStyles}
              onMouseEnter={e => e.target.style.color = '#4a9eff'}
              onMouseLeave={e => e.target.style.color = location.pathname === '/contact' ? '#4a9eff' : '#a8c5e6'}
            >
              Contact
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{
              display: 'none',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.5rem',
            }}
            className="mobile-menu-btn"
          >
            <div style={{
              width: '24px',
              height: '2px',
              background: '#ffffff',
              marginBottom: '6px',
              transition: 'all 0.3s ease',
              transform: mobileMenuOpen ? 'rotate(45deg) translate(5px, 5px)' : 'none',
            }} />
            <div style={{
              width: '24px',
              height: '2px',
              background: '#ffffff',
              marginBottom: '6px',
              opacity: mobileMenuOpen ? 0 : 1,
              transition: 'opacity 0.3s ease',
            }} />
            <div style={{
              width: '24px',
              height: '2px',
              background: '#ffffff',
              transition: 'all 0.3s ease',
              transform: mobileMenuOpen ? 'rotate(-45deg) translate(5px, -5px)' : 'none',
            }} />
          </button>
        </nav>

        {/* Mobile Menu */}
        <div style={{
          display: mobileMenuOpen ? 'flex' : 'none',
          flexDirection: 'column',
          gap: '1rem',
          padding: '1.5rem 0',
          borderTop: '1px solid rgba(74, 158, 255, 0.1)',
          marginTop: '1rem',
        }} className="mobile-menu">
          <Link to="/" style={navLinkStyles}>Home</Link>
          
          <div>
            <button 
              onClick={() => setStrategiesOpen(!strategiesOpen)}
              style={{
                ...navLinkStyles,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                width: '100%',
              }}
            >
              Strategies
              <svg 
                width="10" 
                height="6" 
                viewBox="0 0 10 6" 
                fill="none"
                style={{
                  transform: strategiesOpen ? 'rotate(180deg)' : 'rotate(0)',
                  transition: 'transform 0.2s ease',
                }}
              >
                <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            
            {strategiesOpen && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '0.5rem',
                marginTop: '1rem',
                paddingLeft: '1rem',
              }}>
                {strategies.map((strategy) => (
                  <a
                    key={strategy.symbol}
                    href={strategy.path}
                    style={{
                      color: '#6b9bc9',
                      textDecoration: 'none',
                      fontSize: '0.8rem',
                      padding: '0.5rem',
                    }}
                  >
                    <span style={{ color: '#4a9eff', fontWeight: 600 }}>{strategy.symbol}</span> {strategy.name}
                  </a>
                ))}
              </div>
            )}
          </div>
          
          <Link to="/service" style={navLinkStyles}>Our Service</Link>

          {/* Portfolio Tracker — mobile (single link) */}
          <a href={trackerPath} style={navLinkStyles}>Portfolio Tracker</a>

          <Link to="/members" style={navLinkStyles}>Members</Link>
          <Link to="/subscription" style={navLinkStyles}>Subscribe</Link>
          <Link to="/contact" style={navLinkStyles}>Contact</Link>
        </div>
      </header>

      {/* Main Content */}
      <main>
        {children}
      </main>

      {/* Footer */}
      <footer style={{
        padding: '3rem 5%',
        borderTop: '1px solid rgba(74, 158, 255, 0.1)',
        background: 'rgba(10, 22, 40, 0.5)',
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1.5rem',
        }}>
          <div style={{
            display: 'flex',
            gap: '2rem',
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}>
            <Link to="/" style={{ color: '#6b9bc9', textDecoration: 'none', fontSize: '0.85rem' }}>Home</Link>
            <Link to="/service" style={{ color: '#6b9bc9', textDecoration: 'none', fontSize: '0.85rem' }}>Our Service</Link>
            <Link to="/members" style={{ color: '#6b9bc9', textDecoration: 'none', fontSize: '0.85rem' }}>Members</Link>
            <Link to="/subscription" style={{ color: '#6b9bc9', textDecoration: 'none', fontSize: '0.85rem' }}>Subscribe</Link>
            <Link to="/contact" style={{ color: '#6b9bc9', textDecoration: 'none', fontSize: '0.85rem' }}>Contact</Link>
            <Link to="/terms" style={{ color: '#6b9bc9', textDecoration: 'none', fontSize: '0.85rem' }}>Terms</Link>
            <Link to="/privacy" style={{ color: '#6b9bc9', textDecoration: 'none', fontSize: '0.85rem' }}>Privacy</Link>
          </div>

          {/* Social — follow on X */}
          <a
            href="https://x.com/Parametrix42125"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Parametrix Capital on X"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              padding: '0.55rem 1rem',
              border: '1px solid rgba(74, 158, 255, 0.2)',
              borderRadius: '999px',
              color: '#a8c5e6',
              textDecoration: 'none',
              fontSize: '0.8rem',
              letterSpacing: '0.04em',
              transition: 'border-color 0.2s ease, color 0.2s ease, background 0.2s ease',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = '#4a9eff';
              e.currentTarget.style.color = '#ffffff';
              e.currentTarget.style.background = 'rgba(74, 158, 255, 0.08)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = 'rgba(74, 158, 255, 0.2)';
              e.currentTarget.style.color = '#a8c5e6';
              e.currentTarget.style.background = 'transparent';
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644z"/>
            </svg>
            Follow on X
            <span style={{ color: '#6b9bc9', fontSize: '0.75rem' }}>@Parametrix42125</span>
          </a>
          <p style={{
            color: '#4a6a8a',
            fontSize: '0.8rem',
            margin: 0,
            textAlign: 'center',
          }}>
            © {new Date().getFullYear()} Parametrix Capital. All rights reserved.
          </p>
        </div>
      </footer>

      {/* CSS for responsive design */}
      <style>{`
        @media (max-width: 900px) {
          .desktop-nav {
            display: none !important;
          }
          .mobile-menu-btn {
            display: block !important;
          }
        }
      `}</style>
    </div>
  );
}
